import { useEffect, useState } from "react";
import { Menu, X, ArrowUpRight, Download } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/data";

const BROCHURE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/brochure`;

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-bone/85 border-b border-steel"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => handleNav("top")}
          className="flex items-center gap-2.5 group"
        >
          <span className="w-9 h-9 bg-ink text-bone grid place-items-center font-heading font-black text-sm tracking-tighter group-hover:bg-rust transition-colors">
            S
          </span>
          <div className="leading-none">
            <div className="font-heading font-black text-base md:text-lg tracking-tight text-ink">
              {COMPANY.name}
            </div>
            <div className="font-mono text-[10px] tracking-widest text-graphite uppercase">
              EST. {COMPANY.established}
            </div>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => handleNav(l.id)}
              className="px-4 py-2 font-mono text-xs uppercase tracking-[0.18em] text-ink hover:text-rust transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-cta-brochure"
            href={BROCHURE_URL}
            download="SVP-INFRA-Brochure.pdf"
            className="hidden md:inline-flex items-center gap-2 border border-ink text-ink px-4 py-2.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-ink hover:text-bone transition-colors"
          >
            <Download className="w-3.5 h-3.5" strokeWidth={2.5} />
            Brochure
          </a>
          <button
            data-testid="nav-cta-contact"
            onClick={() => handleNav("contact")}
            className="hidden md:inline-flex items-center gap-2 bg-ink text-bone px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em] hover:bg-rust transition-colors"
          >
            Start a Project
            <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 grid place-items-center border border-ink text-ink hover:bg-ink hover:text-bone transition-colors"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden absolute top-full inset-x-0 bg-bone border-b border-ink"
        >
          <div className="px-5 py-4 flex flex-col">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => handleNav(l.id)}
                className="text-left py-3 border-b border-steel last:border-0 font-heading font-bold text-lg uppercase tracking-tight text-ink hover:text-rust"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-mobile-cta"
              onClick={() => handleNav("contact")}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-ink text-bone px-5 py-3 font-mono text-xs uppercase tracking-[0.18em]"
            >
              Start a Project <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
            <a
              data-testid="nav-mobile-brochure"
              href={BROCHURE_URL}
              download="SVP-INFRA-Brochure.pdf"
              className="mt-2 inline-flex items-center justify-center gap-2 border border-ink text-ink px-5 py-3 font-mono text-xs uppercase tracking-[0.18em]"
            >
              <Download className="w-3.5 h-3.5" /> Download Brochure
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
